"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="85867" className="flex gap-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-mono font-medium text-foreground/70 hover:text-primary transition-colors relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100 pb-1">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-card/95 backdrop-blur-xl border-b border-primary/20 shadow-2xl md:hidden flex flex-col p-4 gap-4 animate-in slide-in-from-top-2">
      {loading ? <div className="flex flex-col gap-4">
          {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted/20 animate-pulse rounded-md" />)}
        </div> : <div className="flex flex-col gap-4">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="block text-lg font-mono font-medium text-foreground/90 hover:text-primary transition-colors" onClick={onClose}>
                {item.label}
              </Link>
            </div>)}
        </div>}
      <Button className="w-full shadow-lg shadow-primary/20 mt-2 font-mono bg-primary text-primary-foreground hover:bg-primary/90" onClick={onClose}>
        Υποστήριξη
      </Button>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 dark ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-primary/10" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex items-center z-10">
          <div className="text-xl font-bold font-mono text-primary">DS</div>
        </Link>

        {}
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="36">
            <DesktopMenuConsumer />
          </MenuProvider>
          <Button variant="outline" className="shadow-lg shadow-primary/10 border-primary/30 hover:border-primary hover:bg-primary/5 hover:shadow-primary/30 font-mono transition-all text-primary">
            Υποστήριξη
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="md:hidden text-foreground/80 hover:text-primary hover:bg-primary/10" onClick={toggleMobileMenu} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>

        {}
        <MenuProvider menu_id="36">
          <MobileMenuConsumer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </header>;
}